import { Cell, Pie, PieChart } from 'recharts';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Gender,
  GetEventDemographicsStatsQuery as GetEventDemographicsStats,
} from '__generated__/graphql';

import { FilterIndicator, StatBar } from './Layout';
import { assetUrl } from '../../common/helpers';
import { useColors } from './helpers';
import UI from '../../common/UI';
import useLocale from '../../common/useLocale';

type Event = GetEventDemographicsStats['event'];

export interface AgeGroupStatsProps {
  event: Event;
}

const AgeGroupStats = memo(({ event }: AgeGroupStatsProps) => {
  const { t } = useTranslation();
  const { formatInteger, formatNumber } = useLocale();

  const formatPercentage = (number: number) => (
    `${formatNumber(number, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
  );

  const barColors = useColors(-100);
  const labelColors = useColors();

  const averageAge = event.analytics.age_groups.average_age;
  const { groups } = event.analytics.age_groups;

  const maxAgeGroupSize = Math.max(...Object.values(groups).map(({ total }) => total));

  const genders = [
    Gender.male,
    Gender.female,
    Gender.neutral,
  ];

  const getValues = (group: (typeof groups)[number]) => {
    const values = genders.map((gender) => group[gender]);
    const sum = values.reduce((sum, count) => sum + count, 0);
    values.push(group.total - sum); // Unknowns
    return values;
  };

  const unknownGenderTitle = t('unknown');

  const genderTitles = [
    ...genders.map((gender) => t(`common:genders.${gender}`)),
    unknownGenderTitle,
  ];

  const genderCounts = genders.reduce((counts, gender) => ({
    ...counts,
    [gender]: groups.reduce((sum, group) => sum + group[gender], 0),
  }), {} as { [key in Gender]: number; });

  /**
   * Number of participants whose gender is unknown.
   */
  const unknownGenderCount = event.registrations_count
   - Object.values(genderCounts).reduce((sum, count) => sum + count, 0);

  const genderData = Object.keys(genderCounts).reduce((data, gender: keyof typeof genderCounts) => [
    ...data,
    { name: gender, count: genderCounts[gender] },
  ], []);

  if (unknownGenderCount > 0) {
    genderData.push({
      name: 'unknown',
      count: unknownGenderCount,
    });
  }

  /**
   * Number of participants whose age is unknown.
   */
  const unknownAgeCount = groups.length === 0
    ? event.registrations_count
    : groups.filter((group) => group.min_age === null && group.max_age === null)[0]?.total || 0;

  const hasGenderData = event.registrations_count - unknownGenderCount > 0;
  const hasAgeData = event.registrations_count - unknownAgeCount > 0;
  const hasData = hasGenderData || hasAgeData;

  return (
    <UI.Card sc={{ hasPanels: true }}>
      <UI.GridContainer sc={{ gutter: 0.75 }} style={{ height: '100%' }}>
        <UI.Div>
          <UI.FlexContainer
            sc={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <UI.H4>
              {hasAgeData && !hasGenderData
                ? t('age')
                : (!hasAgeData && hasGenderData
                  ? t('gender')
                  : t('age_and_gender'))}
            </UI.H4>
            <FilterIndicator sc={{ ml: 2 }} style={{ marginRight: 'auto' }} />
            {averageAge !== null && (
              <UI.Small sc={{ muted: true }}>
                {t('average_age')}
                :
                {' '}
                {formatNumber(averageAge, { maximumFractionDigits: 1 })}
              </UI.Small>
            )}
          </UI.FlexContainer>
          {!hasData && (
            <UI.Div sc={{ color: 'gray.500', mt: 1 }}>
              {t('age_groups_description')}
            </UI.Div>
          )}
        </UI.Div>

        {hasAgeData && (
          <>
            <UI.GridContainer sc={{ alignVertical: 'center' }}>
              <UI.GridContainer sc={{ gutter: 0 }}>
                {groups.map((group, index) => {
                  const values = getValues(group);
                  const sum = values.reduce((sum, count) => sum + count, 0);

                  return (
                    <UI.GridContainer
                      sc={{
                        columns: '75px 1fr 75px 48px',
                        gutter: 0,
                        alignVertical: 'center',
                      }}
                      key={index}
                    >
                      <UI.Div sc={{ fontWeight: 500, color: 'gray.800', small: true }}>
                        {group.min_age !== null ? `${group.min_age}–${group.max_age}` : t('unknown')}
                      </UI.Div>
                      <UI.Div sc={{ pr: 6 }}>
                        <StatBar
                          values={values}
                          max={maxAgeGroupSize}
                          // Reversing the colors makes the entire bar green instead of gray when there are no genders
                          colors={!hasGenderData ? [...barColors].reverse() : barColors}
                          hideContent
                        >
                          {hasGenderData && sum > 0 && (
                            <UI.Label
                              sc={{ background: 'white', noWrap: true }}
                              style={{ boxShadow: '1px 1px 2px rgba(0, 0, 0, .08)' }}
                            >
                              <UI.Delimit delimiter={<UI.Span sc={{ muted: true }}> / </UI.Span>}>
                                {values.map((count, index) => count > 0 && (
                                  <UI.Small key={index}>
                                    <UI.Span style={{ color: labelColors[index], fontWeight: 500 }}>
                                      {formatInteger(count)}
                                      {' '}
                                      {genderTitles[index].slice(0, 1)}
                                    </UI.Span>
                                  </UI.Small>
                                ))}
                              </UI.Delimit>
                            </UI.Label>
                          )}
                        </StatBar>
                      </UI.Div>
                      <UI.Div>
                        {formatInteger(group.total)}
                      </UI.Div>
                      <UI.Div sc={{ muted: true, small: true }}>
                        {formatPercentage((group.total / event.registrations_count) * 100)}
                      </UI.Div>
                    </UI.GridContainer>
                  );
                })}
              </UI.GridContainer>
            </UI.GridContainer>

            {hasGenderData && (
              <UI.Div sc={{ textAlign: 'center', small: true }} style={{ marginTop: hasAgeData && 'auto' }}>
                <UI.FlexContainer sc={{ justifyContent: ['center', 'space-around'], wrap: 'wrap' }}>
                  {genders.map((gender, index) => genderCounts[gender] > 0 && (
                    <UI.Span
                      sc={{ noWrap: true, mx: 0.5 }}
                      key={gender || 'unknown'}
                    >
                      <UI.Span sc={{ fontWeight: 500 }} style={{ color: labelColors[index] }}>
                        {genderTitles[index]}
                        {': '}
                      </UI.Span>
                      {formatInteger(genderCounts[gender] || 0)}
                      {event.registrations_count > 0 && (
                        <UI.Span sc={{ muted: true }}>
                          {' ('}
                          {formatPercentage((genderCounts[gender] / event.registrations_count) * 100)}
                          )
                        </UI.Span>
                      )}
                    </UI.Span>
                  ))}
                  {unknownGenderCount > 0 && (
                    <UI.Span sc={{ noWrap: true, mx: 0.5 }}>
                      <UI.Span sc={{ fontWeight: 500 }} style={{ color: labelColors[3] }}>
                        {unknownGenderTitle}
                        {': '}
                      </UI.Span>
                      {formatInteger(unknownGenderCount)}
                      {event.registrations_count > 0 && (
                        <UI.Span sc={{ muted: true }}>
                          {' ('}
                          {formatPercentage((unknownGenderCount / event.registrations_count) * 100)}
                          )
                        </UI.Span>
                      )}
                    </UI.Span>
                  )}
                </UI.FlexContainer>
              </UI.Div>
            )}
          </>
        )}

        {!hasAgeData && hasGenderData && (
          <UI.GridContainer>
            <UI.GridContainer
              sc={{ columns: '120px 1fr', alignVertical: 'center', gutter: 0 }}
              style={{ marginBottom: 'auto' }}
            >
              <UI.Div>
                <PieChart width={90} height={90}>
                  <Pie
                    data={genderData}
                    dataKey="count"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={45}
                    startAngle={-270}
                  >
                    {genderData.map((entry, index) => (
                      <Cell fill={barColors[index]} key={index} />
                    ))}
                  </Pie>
                </PieChart>
              </UI.Div>

              <UI.Div>
                {genders.map((gender, index) => genderCounts[gender] > 0 && (
                  <UI.GridContainer sc={{ columns: '6fr 3fr 2fr', gutter: 0 }} key={gender}>
                    <UI.Div sc={{ fontWeight: 500 }} style={{ color: labelColors[index] }}>
                      {genderTitles[index]}
                    </UI.Div>
                    <UI.Div>
                      {formatInteger(genderCounts[gender] || 0)}
                    </UI.Div>
                    {event.registrations_count > 0 && (
                      <UI.Div sc={{ muted: true }}>
                        {formatPercentage((genderCounts[gender] / event.registrations_count) * 100)}
                      </UI.Div>
                    )}
                  </UI.GridContainer>
                ))}
                {unknownGenderCount > 0 && (
                  <UI.GridContainer sc={{ columns: '6fr 3fr 2fr', gutter: 0 }}>
                    <UI.Div sc={{ fontWeight: 500 }} style={{ color: labelColors[3] }}>
                      {unknownGenderTitle}
                    </UI.Div>
                    <UI.Div>
                      {formatInteger(unknownGenderCount)}
                    </UI.Div>
                    {event.registrations_count > 0 && (
                      <UI.Div sc={{ muted: true }}>
                        {formatPercentage((unknownGenderCount / event.registrations_count) * 100)}
                      </UI.Div>
                    )}
                  </UI.GridContainer>
                )}
              </UI.Div>
            </UI.GridContainer>
          </UI.GridContainer>
        )}

        {!hasData && (
          <UI.Div>
            <UI.FlexContainer sc={{ justifyContent: 'center', mb: 2 }}>
              <img src={assetUrl('images/dashboard-age-groups.svg')} height={60} alt={t('age_and_gender')} />
            </UI.FlexContainer>
            <UI.Div sc={{ textAlign: 'center', color: 'gray.400' }}>
              {t('no_data_available')}
            </UI.Div>
          </UI.Div>
        )}
      </UI.GridContainer>
    </UI.Card>
  );
});

export default AgeGroupStats;
